import { CollapsiblePanel, FormCheckboxInput, FormNumberInput, FormTextAreaInput, FormTextInput, Heading } from "cai-fusion";
import { useIntegration } from "../../../../contexts/IntegrationContext";
import { debugLog } from "../../../../../../configs/loggingConfig";

const IntegrationConfiguration = () => {
    
    const { currIntegration, setCurrIntegration, currConfigItem, setCurrConfigItem } = useIntegration();

    const changeIntegrationData = (field, newVal) => {
        let newIntegrationItem = {...currIntegration}
        newIntegrationItem[field] = newVal;
        debugLog.log(newIntegrationItem)
        setCurrIntegration(newIntegrationItem);
    }

    const changeConfigData = (id, newVal) => {
        let newConfigItem = {...currConfigItem}
        newConfigItem.fields.find((item) => item.id === id).value = newVal;
        setCurrConfigItem(newConfigItem);
    }

    return (
        <form className="o-form">
            <Heading size={4}>{`${currConfigItem?.name ?? ""} Integration`}</Heading>
            <p className="a-muted-text l-mb-xs">Configure your integration and its' integration-specific settings.</p>

            {/* Basic Integration Info - Name & Description, shared by all */}
            <FormTextInput 
                displayName="Name"
                required
                fieldId="name"
                formValue={currIntegration?.name ?? ""}
                onInputChange={(fieldId, newValue) => changeIntegrationData(fieldId, newValue)}
            />
            <FormTextAreaInput 
                displayName="Description"
                fieldId="description"
                formValue={currIntegration?.description ?? ""}
                onInputChange={(fieldId, newValue) => changeIntegrationData(fieldId, newValue)}
            />

            {currIntegration?.integrationType === "HttpApi" && // Rest API
            <>
                {/* 
                    endpoint - string
                    body - string
                    timeout - number (int)
                    baseUrl - string
                    authenticationType - options
                    - None
                    - ApiKey
                    - BasicAuth
                    - BearerTokenAuth
                    - OAuth2
                    authenticationConfig - string
                */}
                <FormTextInput 
                    displayName="Endpoint"
                    fieldId="endpoint"
                    formValue={currIntegration?.endpoint ?? ""}
                    onInputChange={(fieldId, newValue) => changeIntegrationData(fieldId, newValue)}
                />
                <FormTextAreaInput 
                    displayName="Body"
                    fieldId="body"
                    rows={5}
                    formValue={currIntegration?.body ?? ""}
                    onInputChange={(fieldId, newValue) => changeIntegrationData(fieldId, newValue)}
                />
                <FormNumberInput 
                    displayName="Timeout (in seconds)"
                    fieldId="timeout"
                    formValue={currIntegration?.timeout ?? 0}
                    onInputChange={(fieldId, newValue) => changeIntegrationData(fieldId, newValue)}
                />
                <div className="m-form-group">
                    <label htmlFor="method" className="a-label">Method</label>
                    <select 
                        className="a-select-input" 
                        value={currIntegration?.method ?? undefined} 
                        onChange={(e) => changeIntegrationData("method", e.target.value)}
                    >
                        <option value="GET">GET</option>
                        <option value="POST">POST</option>
                        <option value="PUT">PUT</option>
                        <option value="PATCH">PATCH</option>
                        <option value="DELETE">DELETE</option>
                        <option value="CONNECT">CONNECT</option>
                    </select>
                </div>
                <FormTextInput 
                    displayName="Base URL"
                    fieldId="baseUrl"
                    formValue={currIntegration?.baseUrl ?? ""}
                    onInputChange={(fieldId, newValue) => changeIntegrationData(fieldId, newValue)}
                />
                {/* method - GET POST PUT PATCH CONNECT DELETE */}
                <div className="m-form-group">
                    <label htmlFor="authenticationType" className="a-label">Authentication Type</label>
                    <select 
                        className="a-select-input" 
                        value={currIntegration?.authenticationType ?? undefined} 
                        onChange={(e) => changeIntegrationData("authenticationType", e.target.value)}
                    >
                        <option value="None">None</option>
                        <option value="ApiKey">API Key</option>
                        <option value="BasicAuth">Basic Auth</option>
                        <option value="BearerTokenAuth">Bearer Token Auth</option>
                        <option value="OAuth2">OAuth2</option>
                    </select>
                </div>
                <FormTextAreaInput 
                    displayName="Authentication Configuration"
                    fieldId="authenticationConfig"
                    formValue={currIntegration?.authenticationConfig ?? ""}
                    onInputChange={(fieldId, newValue) => changeIntegrationData(fieldId, newValue)}
                />
            </>
            }

            {currIntegration?.integrationType === "Database" && // SQL Server
            <>
                {/* 
                    databaseProvider - options
                    - SqlServer
                    - PostgreSQL
                    - MySQL
                    - Oracle
                    baseUrl - string
                    authenticationType - options
                    - None
                    - ApiKey
                    - BasicAuth
                    - BearerTokenAuth
                    - OAuth2
                    authenticationConfig - string
                */}
                <div className="m-form-group">
                    <label htmlFor="databaseProvider" className="a-label">Database Provider</label>
                    <select 
                        className="a-select-input" 
                        value={currIntegration?.databaseProvider ?? undefined} 
                        onChange={(e) => changeIntegrationData("databaseProvider", e.target.value)}
                    >
                        <option value="SqlServer">SQL Server</option>
                        <option value="PostgreSQL">PostgreSQL</option>
                        <option value="MySQL">MySQL</option>
                        <option value="Oracle">Oracle</option>
                    </select>
                </div>
                <FormTextInput 
                    displayName="Base URL"
                    fieldId="baseUrl"
                    formValue={currIntegration?.baseUrl ?? ""}
                    onInputChange={(fieldId, newValue) => changeIntegrationData(fieldId, newValue)}
                />
                <div className="m-form-group">
                    <label htmlFor="authenticationType" className="a-label">Authentication Type</label>
                    <select 
                        className="a-select-input" 
                        value={currIntegration?.authenticationType ?? undefined} 
                        onChange={(e) => changeIntegrationData("authenticationType", e.target.value)}
                    >
                        <option value="None">None</option>
                        <option value="ApiKey">API Key</option>
                        <option value="BasicAuth">Basic Auth</option>
                        <option value="BearerTokenAuth">Bearer Token Auth</option>
                        <option value="OAuth2">OAuth2</option>
                    </select>
                </div>
                <FormTextAreaInput 
                    displayName="Authentication Configuration"
                    fieldId="authenticationConfig"
                    formValue={currIntegration?.authenticationConfig ?? ""}
                    onInputChange={(fieldId, newValue) => changeIntegrationData(fieldId, newValue)}
                />
            </>
            }

            {/* All items that aren't advanced will be shown regardless... */}
            {currConfigItem?.fields?.filter((field) => !field.isAdvanced).map((field) => (
                <ConfigurationFieldItem field={field} />
            ))}

            {/* ...but all advanced items will be in a collapsible panel. */}
            {currConfigItem?.fields?.filter((field) => field.isAdvanced).length > 0 &&
            <div className="o-block o-block--body">
                <CollapsiblePanel label="Advanced Settings">
                    {currConfigItem?.fields?.filter((field) => field.isAdvanced).map((field) => (
                        <ConfigurationFieldItem field={field} />
                    ))}
                </CollapsiblePanel>
            </div>
            }
        </form>
    )
}

const ConfigurationFieldItem = ({field}) => {
    
    const {currConfigItem, setCurrConfigItem} = useIntegration();

    const changeConfigData = (newVal) => {
        let newConfigItem = {...currConfigItem}
        newConfigItem.fields.find((item) => item.id === field.id).value = newVal;
        setCurrConfigItem(newConfigItem);
    }
    
    return (
        <>
            {field?.fieldType === "text" &&
                <FormTextInput
                    key={field.id}
                    displayName={field?.name}
                    description={field?.description}
                    required={field?.isRequired}
                    formValue={field?.value}
                    onInputChange={(fieldId, newVal) => changeConfigData(newVal)}
                />
            }
            {field?.fieldType === "textarea" &&
                <FormTextAreaInput 
                    key={field.id}
                    displayName={field?.name}
                    description={field?.description}
                    required={field?.isRequired}
                    formValue={field?.value}
                    onInputChange={(fieldId, newVal) => changeConfigData(newVal)}
                />
            }
            {field?.fieldType === "checkbox" &&
                <FormCheckboxInput 
                    key={field.id}
                    displayName={field?.name}
                    required={field?.isRequired}
                    formValue={field?.value}
                    onInputChange={(fieldId, newVal) => changeConfigData(newVal)}
                />
            }
            {field?.fieldType === "dropdown" &&
                <></>
            }
        </>
    )
}

export default IntegrationConfiguration;